import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dcMessagesApi } from "../helpers/dck_helper";
import { messageQuery, categoryCountsToTabs } from "../helpers/messageContract";
import { useSessionStore } from "../stores/sessionStore";
import i18n from "../i18n";
import { GENERIC_ERROR_MSG } from "../helpers/errorMessages";

/**
 * 訊息查詢頁與小鈴鐺共用的 DcMessage 查詢（notify-api 票 03–06）。
 *
 * queryKey 一律掛在 `MESSAGES_QUERY_KEY` 底下:已讀寫入成功後只要失效這一把,
 * 列表、分類筆數、未讀數三組一起重抓 —— 頁面與鈴鐺才會互相同步(spec「已讀寫入成功後…」)。
 */
export const MESSAGES_QUERY_KEY = "dcMessages";

/**
 * 登入者的 customer id(GetToken 回的 customer_id;身分組裝見 sessionStore)。
 * 取不到(沒登入、開發用的切角色身分 id 0)就是 null —— 訊息查詢不會送出,
 * 這條降級要吵(ADR-0004),畫面也要講出來,不能長得像「你沒有訊息」。
 * warn 放在 effect(不在 render 裡做副作用),每次掛載時取不到都會講,不會被第一次用掉。
 */
export const useMessageOwnerId = () => {
  const id = useSessionStore((s) => s.user?.id || null);
  useEffect(() => {
    if (!id) console.warn("[messages] 取不到登入者的 customer id,DcMessage 查詢不送出(不帶 targetCustomerId 會拿到全部人的訊息)。");
  }, [id]);
  return id;
};

/**
 * 訊息列表(伺服器分頁)。沒有登入者就不發請求 —— 不帶 targetCustomerId 會拿到全部人的訊息。
 *   const q = useMessageList({ tab, keyword, page, pageSize });
 */
export function useMessageList({ tab, keyword, page, pageSize, isRead } = {}) {
  const customerId = useMessageOwnerId();
  const params = messageQuery({ customerId, tab, keyword, page, pageSize, isRead });
  return useQuery({
    queryKey: [MESSAGES_QUERY_KEY, "list", params],
    queryFn: () => dcMessagesApi.getAll(params),
    enabled: !!params,
    // 只有「翻頁」保留上一頁避免閃爍;換分頁籤或關鍵字時不沿用 —— 否則新條件的結果回來前,
    // 畫面上掛著的是上一個分頁的訊息(票 04「不拿到舊結果」)
    placeholderData: (prev, prevQuery) => (sameExceptPage(prevQuery?.queryKey?.[2], params) ? prev : undefined),
  });
}

const sameExceptPage = (a, b) => {
  if (!a || !b) return false;
  const strip = ({ pageIndex, ...rest }) => JSON.stringify(rest);
  return strip(a) === strip(b);
};

/**
 * 各分頁籤旁的筆數:六類用 `GetCategoryCounts`(以 value 1–6 對到分頁),「全部」另打一支
 * 不帶種類／關鍵字的 GetAll(pageSize 1,只取 total_count)。
 *
 * 「全部」不直接拿列表的 total_count:列表會跟著目前分頁與關鍵字變,在「審核流程」分頁時
 * 它就是審核流程的筆數;而六類的筆數本來就不受關鍵字影響 —— 七個數字要同一個口徑。
 * 種類不在六類的訊息(SIT 的 0)只算進「全部」。
 *
 * 回 `{ all?, unfilled?, … }`;哪一支失敗,那幾個分頁就不顯示數字(不擋整頁)。
 */
export function useMessageTabCounts() {
  const customerId = useMessageOwnerId();
  const enabled = !!customerId;
  const byCategory = useQuery({
    queryKey: [MESSAGES_QUERY_KEY, "categoryCounts", customerId],
    queryFn: () => dcMessagesApi.getCategoryCounts({ targetCustomerId: customerId }),
    enabled,
  });
  const allParams = messageQuery({ customerId, page: 1, pageSize: 1 });
  const all = useQuery({
    queryKey: [MESSAGES_QUERY_KEY, "list", allParams],
    queryFn: () => dcMessagesApi.getAll(allParams),
    enabled,
  });
  const counts = byCategory.data ? categoryCountsToTabs(byCategory.data) : {};
  if (all.data) counts.all = all.data.total_count;
  return counts;
}

/** 登入者的未讀數(GetUnreadCount;targetCustomerId 必填)。訊息頁的「全部標示已讀」與小鈴鐺共用 */
export function useUnreadCount() {
  const customerId = useMessageOwnerId();
  return useQuery({
    queryKey: [MESSAGES_QUERY_KEY, "unreadCount", customerId],
    queryFn: () => dcMessagesApi.getUnreadCount(customerId),
    enabled: !!customerId,
  });
}

/**
 * 標示已讀(MarkAsRead／MarkAllAsRead)。成功後失效整把 `dcMessages` ——
 * 列表、分類筆數、未讀數一起重抓,訊息頁與鈴鐺互相同步(spec「已讀寫入成功後…」)。
 * 失敗不動快取:紅點與筆數維持原狀,呼叫端提示後可再試。
 */
export function useMarkMessagesRead() {
  const customerId = useMessageOwnerId();
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: [MESSAGES_QUERY_KEY] });
  return {
    markOne: useMutation({ mutationFn: (id) => dcMessagesApi.markAsRead(id), onSuccess }),
    // 範圍:稿上「全部標示已讀」在工具列(分頁之上)、沒有註記 → 清全部,不帶 messageCategory(spec 補充)
    markAll: useMutation({
      // 沒有登入者就不送:不帶 targetCustomerId 會清掉**全部人的**未讀
      mutationFn: () => (customerId
        ? dcMessagesApi.markAllAsRead({ targetCustomerId: customerId })
        : Promise.reject(i18n.t("notify.noOwner"))),
      onSuccess,
    }),
  };
}

/**
 * 寫入失敗的原因文案。攔截器轉譯過的是字串(可安全顯示);其他(程式錯、非 axios 錯誤)一律制式文案,
 * 畫面不出現 `[object Object]`／`Error: …` 這類技術字(同 IpsPage／RoomForm 的慣例)。
 */
export const readFailureReason = (err) => (typeof err === "string" && err ? err : GENERIC_ERROR_MSG);
